import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { UpdateCardPaymentSettings, UpdateRiskRules } from "@/api";
import { settingsApi } from "@/api";
import { QUERY_KEYS } from "@/constants";

export const usePlatformSettings = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_PLATFORM_SETTINGS],
    queryFn: () => settingsApi.platform(),
    enabled,
    select: res => res.data,
  });

/**
 * Ulushni saqlash.
 *
 * Hisobotlar ham yangilanadi: o'qituvchi ulushi sahifalarida "joriy foiz"
 * ko'rsatiladi. Eski to'lovlarning ulushi esa O'ZGARMAYDI — u to'lov
 * paytida muzlatilgan.
 */
export const useUpdatePlatformSettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commissionBps: number) => settingsApi.updatePlatform({ commissionBps }),
    onSuccess: res => {
      queryClient.setQueryData([QUERY_KEYS.GET_PLATFORM_SETTINGS], res);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TEACHER_REPORT] });
      toast.success(t("SETTINGS.SAVED"));
    },
  });
};

export const useRiskRules = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_RISK_RULES],
    queryFn: () => settingsApi.riskRules(),
    enabled,
    select: res => res.data,
  });

/**
 * Xavf chegaralarini saqlash. Xavf ro'yxatlari va kartochkalar shu
 * raqamlardan hisoblanadi — ular ham qayta so'raladi.
 */
export const useUpdateRiskRules = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateRiskRules) => settingsApi.updateRiskRules(dto),
    onSuccess: res => {
      queryClient.setQueryData([QUERY_KEYS.GET_RISK_RULES], res);
      for (const key of [
        QUERY_KEYS.GET_AT_RISK,
        QUERY_KEYS.GET_STUDENT_REPORTS,
        QUERY_KEYS.GET_STUDENT_REPORT,
        QUERY_KEYS.GET_TEACHER_REPORTS,
        QUERY_KEYS.GET_TEACHER_REPORT,
        QUERY_KEYS.GET_COURSE_REPORTS,
      ]) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
      toast.success(t("SETTINGS.RISK_SAVED"));
    },
  });
};

/** Kartaga to'lov sozlamasi (faqat admin). */
export const useCardPaymentSettings = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_CARD_PAYMENT_SETTINGS],
    queryFn: () => settingsApi.cardPayments(),
    enabled,
    select: res => res.data,
  });

export const useUpdateCardPaymentSettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateCardPaymentSettings) => settingsApi.updateCardPayments(dto),
    onSuccess: res => {
      queryClient.setQueryData([QUERY_KEYS.GET_CARD_PAYMENT_SETTINGS], res);
      toast.success(t("SETTINGS.SAVED"));
    },
  });
};
